import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useVerifyMfa } from '@/entities/auth';
import { getApiErrorMessage, getApiErrorStatus } from '@/shared/api';
import { Button, Form, HintIcon } from '@/shared/ui';

import { AuthTextField } from '../AuthTextField';
import { AUTH_HINT, AUTH_TEXT } from '../texts';
import { AuthNotice } from './AuthNotice';

/** Сколько живёт mfa_token из ответа `/auth/login`. */
const MFA_TOKEN_TTL_MS = 5 * 60 * 1000;

const schema = z.object({
  code: z.string().trim().min(1, 'Введите код'),
});

type MfaForm = z.infer<typeof schema>;

/**
 * Второй фактор. Одно поле и для 6 цифр из приложения, и для резервного кода
 * — бэк сам различает, что пришло.
 */
export function MfaStep({
  mfaToken,
  issuedAt,
  onLoggedIn,
  onExpired,
  onRestart,
}: {
  mfaToken: string;
  /** Когда пришёл mfa_token, `Date.now()`. */
  issuedAt: number;
  onLoggedIn: () => void;
  onExpired: () => void;
  onRestart: () => void;
}) {
  const form = useForm<MfaForm>({
    resolver: zodResolver(schema),
    defaultValues: { code: '' },
  });
  const verify = useVerifyMfa();
  const [rateLimited, setRateLimited] = useState(false);

  const onSubmit = form.handleSubmit(({ code }) => {
    setRateLimited(false);
    verify.mutate(
      { mfa_token: mfaToken, code },
      {
        onSuccess: onLoggedIn,
        onError: (error) => {
          const status = getApiErrorStatus(error);

          if (status === 401) {
            // На неверный код и на истёкший mfa_token бэк отвечает одинаково —
            // различаем по времени: токен живёт 5 минут.
            if (Date.now() - issuedAt > MFA_TOKEN_TTL_MS) {
              onExpired();
              return;
            }
            form.setError('code', { message: AUTH_TEXT.invalidCode });
            return;
          }
          if (status === 429) {
            setRateLimited(true);
            return;
          }
          toast.error(getApiErrorMessage(error, 'Не удалось проверить код'));
        },
      }
    );
  });

  const busy = verify.isPending;

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="grid gap-4">
        <div className="grid gap-2">
          <AuthTextField
            control={form.control}
            name="code"
            label="Код"
            placeholder="123456"
            autoComplete="one-time-code"
            hint={AUTH_HINT.mfaCode}
            autoFocus
            disabled={busy}
          />
          {/* По инструкции это только подсказка: поле остаётся тем же. */}
          <HintIcon
            text={AUTH_HINT.recoveryCode}
            className="justify-self-start text-left text-sm font-medium text-[var(--primary)] hover:underline"
          >
            Нет доступа к приложению? Введите резервный код
          </HintIcon>
        </div>

        {rateLimited ? (
          <AuthNotice
            tone="error"
            text={AUTH_TEXT.loginRateLimited}
            hint={AUTH_HINT.rateLimited}
          />
        ) : null}

        <Button
          type="submit"
          size="lg"
          disabled={busy}
          className="mt-2 w-full rounded-xl"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Проверяем…
            </>
          ) : (
            <>
              <ShieldCheck className="h-4 w-4" />
              Подтвердить
            </>
          )}
        </Button>

        <button
          type="button"
          onClick={onRestart}
          disabled={busy}
          className="text-sm font-medium text-slate-500 hover:text-slate-700 hover:underline disabled:opacity-60"
        >
          Войти заново
        </button>
      </form>
    </Form>
  );
}
